// IE Hub - FAQ Page
// Displays frequently asked questions about using IE Hub

import type { ReactNode } from 'react';
import { useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Paper
} from '../../../ui';
import DashboardLayout from '../../../components/DashboardLayout';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import faqMarkdown from './ieHubFaq.md?raw';

// FAQ item type
interface FAQItem {
  question: string;
  answer: string;
  category: string;
}

function extractHeadingText(headingLine: string): string {
  const noHashes = headingLine.replace(/^#+\s*/, '').trim();
  const boldMatch = noHashes.match(/^\*\*(.*)\*\*$/);
  return (boldMatch?.[1] ?? noHashes).trim();
}

function nextNonEmptyLine(lines: string[], startIndex: number): string | undefined {
  for (let i = startIndex; i < lines.length; i += 1) {
    if (lines[i]?.trim() !== '') return lines[i];
  }
  return undefined;
}

function isSeparatorLine(line: string): boolean {
  return line.trim() === '---';
}

function isHeadingLine(line: string): boolean {
  return /^(#{2,6})\s+/.test(line.trim());
}

function unescapeMarkdownText(text: string): string {
  // Minimal markdown backslash unescape for this doc (e.g. "\+ Add Company", "7\.")
  return text.replace(/\\([\\`*_{}\[\]()#+\-.!])/g, '$1');
}

function parseFaqMarkdown(markdown: string): FAQItem[] {
  const lines = markdown.split(/\r?\n/);
  const items: FAQItem[] = [];

  let currentCategory: string | null = null;

  for (let i = 0; i < lines.length; i += 1) {
    const rawLine = lines[i] ?? '';
    const line = rawLine.trimEnd();
    const trimmed = line.trim();

    // Category heading is "## **X**" followed (next non-empty) by '---'
    if (/^##\s+\*\*.*\*\*\s*$/.test(trimmed)) {
      const nextLine = nextNonEmptyLine(lines, i + 1);
      if (nextLine !== undefined && isSeparatorLine(nextLine)) {
        currentCategory = extractHeadingText(trimmed);
        continue;
      }
    }

    const isQuestionHeading =
      /^###\s+\*\*.*\*\*\s*$/.test(trimmed) || /^##\s+\*\*.*\*\*\s*$/.test(trimmed);

    if (!isQuestionHeading || currentCategory === null) continue;

    const question = extractHeadingText(trimmed);
    const answerLines: string[] = [];

    // Read until the next separator line `---`, OR until we hit the next category heading
    // (some docs omit the trailing `---` before a new section).
    let j = i + 1;
    for (; j < lines.length; j += 1) {
      const raw = lines[j] ?? '';
      const l = raw.trimEnd();
      const t = l.trim();

      if (isSeparatorLine(t)) {
        j += 1; // consume separator
        break;
      }

      // If we see a category heading (## **X** + next non-empty is ---), stop BEFORE it
      if (/^##\s+\*\*.*\*\*\s*$/.test(t)) {
        const nextLine = nextNonEmptyLine(lines, j + 1);
        if (nextLine !== undefined && isSeparatorLine(nextLine)) {
          break;
        }
      }

      answerLines.push(raw);
    }

    items.push({
      category: currentCategory,
      question,
      answer: answerLines.join('\n').trim()
    });

    // Move outer loop to where we stopped (j is either the next line after `---`,
    // or the category heading line we want to process next)
    i = j - 1;
  }

  return items;
}

function renderInlineMarkdown(text: string): ReactNode[] {
  // Supports: **bold**, *italic*, [label](url)
  const tokenRegex = /(\*\*[^*]+\*\*|\*[^*]+\*|\[[^\]]+\]\([^)]+\))/g;
  const nodes: ReactNode[] = [];

  const src = unescapeMarkdownText(text);

  let lastIndex = 0;
  for (const match of src.matchAll(tokenRegex)) {
    const token = match[0];
    const index = match.index ?? 0;

    if (index > lastIndex) {
      nodes.push(src.slice(lastIndex, index));
    }

    if (token.startsWith('**') && token.endsWith('**')) {
      nodes.push(<strong key={`b-${index}`}>{token.slice(2, -2)}</strong>);
    } else if (token.startsWith('*') && token.endsWith('*')) {
      nodes.push(<em key={`i-${index}`}>{token.slice(1, -1)}</em>);
    } else if (token.startsWith('[')) {
      const linkMatch = token.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
      if (linkMatch) {
        const label = linkMatch[1];
        const href = linkMatch[2];
        nodes.push(
          <Box
            key={`a-${index}`}
            component="a"
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            sx={{ display: 'inline', color: '#004b49', textDecoration: 'underline' }}
          >
            {label}
          </Box>
        );
      } else {
        nodes.push(token);
      }
    } else {
      nodes.push(token);
    }

    lastIndex = index + token.length;
  }

  if (lastIndex < src.length) {
    nodes.push(src.slice(lastIndex));
  }

  return nodes;
}

type MarkdownBlock =
  | { type: 'heading'; level: number; text: string }
  | { type: 'ul'; items: string[] }
  | { type: 'ol'; items: string[] }
  | { type: 'p'; lines: string[] };

function parseMarkdownBlocks(markdown: string): MarkdownBlock[] {
  const lines = markdown.split(/\r?\n/);
  const blocks: MarkdownBlock[] = [];

  let i = 0;
  while (i < lines.length) {
    const line = (lines[i] ?? '').trimEnd();
    const trimmed = line.trim();

    // Skip blank/separator and "empty heading" artifacts like "## " in the source doc
    if (trimmed === '' || isSeparatorLine(trimmed) || /^#{2,6}\s*$/.test(trimmed)) {
      i += 1;
      continue;
    }

    const headingMatch = trimmed.match(/^(#{2,6})\s+(.*)$/);
    if (headingMatch) {
      const hashes = headingMatch[1] ?? '';
      const headingText = headingMatch[2] ?? '';
      blocks.push({
        type: 'heading',
        level: hashes.length,
        text: headingText
      });
      i += 1;
      continue;
    }

    const ulMatch = line.match(/^\s*\*\s+(.*)$/);
    if (ulMatch) {
      const items: string[] = [];
      while (i < lines.length) {
        const l = (lines[i] ?? '').trimEnd();
        if (l.trim() === '') {
          i += 1;
          continue;
        }
        const m = l.match(/^\s*\*\s+(.*)$/);
        if (!m) break;
        items.push(m[1] ?? '');
        i += 1;
      }
      blocks.push({ type: 'ul', items });
      continue;
    }

    const olMatch = line.match(/^\s*\d+\.\s+(.*)$/);
    if (olMatch) {
      const items: string[] = [];
      while (i < lines.length) {
        const l = (lines[i] ?? '').trimEnd();
        if (l.trim() === '') {
          i += 1;
          continue;
        }
        const m = l.match(/^\s*\d+\.\s+(.*)$/);
        if (!m) break;
        items.push(m[1] ?? '');
        i += 1;
      }
      blocks.push({ type: 'ol', items });
      continue;
    }

    // Paragraph
    const paraLines: string[] = [];
    while (i < lines.length) {
      const l = (lines[i] ?? '').trimEnd();
      const t = l.trim();
      if (t === '' || isSeparatorLine(t) || isHeadingLine(t) || /^\s*\*\s+/.test(l) || /^\s*\d+\.\s+/.test(l)) {
        break;
      }
      paraLines.push(lines[i] ?? '');
      i += 1;
    }
    if (paraLines.length > 0) blocks.push({ type: 'p', lines: paraLines });
  }

  return blocks;
}

function MarkdownContent({ markdown }: { markdown: string }) {
  const blocks = useMemo(() => parseMarkdownBlocks(markdown), [markdown]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      {blocks.map((block, idx) => {
        if (block.type === 'heading') {
          return (
            <Typography
              key={`h-${idx}`}
              sx={{
                fontSize: '14px',
                fontWeight: 700,
                color: '#343a40'
              }}
            >
              {renderInlineMarkdown(extractHeadingText(block.text))}
            </Typography>
          );
        }

        if (block.type === 'ul') {
          return (
            <Box key={`ul-${idx}`} component="ul" sx={{ m: 0, pl: 2 }}>
              {block.items.map((item, itemIdx) => (
                <Box key={`ul-${idx}-${itemIdx}`} component="li" sx={{ mb: 0.5 }}>
                  <Typography sx={{ fontSize: '14px', color: '#6c757d', lineHeight: '21px' }}>
                    {renderInlineMarkdown(item)}
                  </Typography>
                </Box>
              ))}
            </Box>
          );
        }

        if (block.type === 'ol') {
          return (
            <Box key={`ol-${idx}`} component="ol" sx={{ m: 0, pl: 2 }}>
              {block.items.map((item, itemIdx) => (
                <Box key={`ol-${idx}-${itemIdx}`} component="li" sx={{ mb: 0.5 }}>
                  <Typography sx={{ fontSize: '14px', color: '#6c757d', lineHeight: '21px' }}>
                    {renderInlineMarkdown(item)}
                  </Typography>
                </Box>
              ))}
            </Box>
          );
        }

        // paragraph
        return (
          <Typography
            key={`p-${idx}`}
            sx={{
              fontSize: '14px',
              color: '#6c757d',
              lineHeight: '21px'
            }}
            component="div"
          >
            {block.lines.map((line, lineIdx) => (
              <Box key={`p-${idx}-${lineIdx}`} component="span">
                {renderInlineMarkdown(line)}
                {lineIdx < block.lines.length - 1 ? <br /> : null}
              </Box>
            ))}
          </Typography>
        );
      })}
    </Box>
  );
}

const FaqPage: React.FC = () => {
  const [expanded, setExpanded] = useState<string | false>(false);

  const handleAccordionChange = (panel: string) => (_event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpanded(isExpanded ? panel : false);
  };

  const faqData: FAQItem[] = useMemo(() => parseFaqMarkdown(faqMarkdown), [faqMarkdown]);

  // Group FAQs by category
  const categories = Array.from(new Set(faqData.map(faq => faq.category)));

  return (
    <DashboardLayout backgroundColor="#f8f9fa">
      <Box sx={{ py: { xs: 2, md: 3 }, display: 'flex', flexDirection: 'column', gap: 4 }}>
        {/* Header Section */}
        <Box>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              fontSize: { xs: '24px', md: '30px' },
              lineHeight: '36px',
              color: '#343a40',
              mb: 0.5
            }}
          >
            Frequently Asked Questions
          </Typography>
          <Typography
            variant="body1"
            sx={{
              fontSize: '16px',
              lineHeight: '24px',
              color: '#6c757d'
            }}
          >
            Find answers to common questions about using IE Hub and managing your finances.
          </Typography>
        </Box>

        {/* FAQ Categories */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {categories.map((category, categoryIndex) => (
            <Box key={categoryIndex}>
              {/* Category Header */}
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  fontSize: '20px',
                  color: '#343a40',
                  mb: 2
                }}
              >
                {category}
              </Typography>

              {/* FAQ Items in Category */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {faqData
                  .filter(faq => faq.category === category)
                  .map((faq, faqIndex) => {
                    const panelId = `panel-${categoryIndex}-${faqIndex}`;
                    return (
                      <Accordion
                        key={faqIndex}
                        expanded={expanded === panelId}
                        onChange={handleAccordionChange(panelId)}
                        sx={{
                          borderRadius: '8px !important',
                          boxShadow: 'none',
                          border: '1px solid #dee2e6',
                          '&:before': {
                            display: 'none'
                          },
                          '&.Mui-expanded': {
                            margin: 0,
                            mb: 1
                          }
                        }}
                      >
                        <AccordionSummary
                          expandIcon={<ExpandMoreIcon sx={{ color: '#004b49' }} />}
                          sx={{
                            minHeight: '56px',
                            '&.Mui-expanded': {
                              minHeight: '56px'
                            },
                            '& .MuiAccordionSummary-content': {
                              margin: '12px 0',
                              '&.Mui-expanded': {
                                margin: '12px 0'
                              }
                            }
                          }}
                        >
                          <Typography
                            sx={{
                              fontWeight: 600,
                              fontSize: '16px',
                              color: '#343a40',
                              lineHeight: 'normal'
                            }}
                          >
                            {faq.question}
                          </Typography>
                        </AccordionSummary>
                        <AccordionDetails
                          sx={{
                            pt: 0,
                            pb: 2,
                            px: 2
                          }}
                        >
                          <MarkdownContent markdown={faq.answer} />
                        </AccordionDetails>
                      </Accordion>
                    );
                  })}
              </Box>
            </Box>
          ))}
        </Box>

        {/* Still Need Help Section */}
        <Paper
          sx={{
            p: 3,
            borderRadius: '12px',
            backgroundColor: '#e7f5f4',
            border: '1px solid #b8e3df',
            mt: 2
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              fontSize: '18px',
              color: '#343a40',
              mb: 1
            }}
          >
            Still need help?
          </Typography>
          <Typography
            variant="body2"
            sx={{
              fontSize: '14px',
              color: '#6c757d',
              lineHeight: '21px',
              mb: 2
            }}
          >
            If you couldn't find the answer you're looking for, visit our Help & Support page for additional resources and contact options.
          </Typography>
          <Box
            component="a"
            href="/#/help-and-support"
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              display: 'inline-block',
              px: 3,
              py: 1.25,
              backgroundColor: '#004b49',
              color: '#ffffff',
              borderRadius: '6px',
              textDecoration: 'none',
              fontSize: '14px',
              fontWeight: 600,
              transition: 'all 0.2s ease',
              '&:hover': {
                backgroundColor: '#003634',
                transform: 'translateY(-1px)',
                boxShadow: '0 2px 8px rgba(0, 75, 73, 0.3)'
              }
            }}
          >
            Visit help & support
          </Box>
        </Paper>
      </Box>
    </DashboardLayout>
  );
};

export default FaqPage;

